import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const DIAS = [
  { weekdayEn: "monday", label: "Segunda", weekday: "Segunda-feira" },
  { weekdayEn: "tuesday", label: "Terça", weekday: "Terça-feira" },
  { weekdayEn: "wednesday", label: "Quarta", weekday: "Quarta-feira" },
  { weekdayEn: "thursday", label: "Quinta", weekday: "Quinta-feira" },
  { weekdayEn: "friday", label: "Sexta", weekday: "Sexta-feira" },
  { weekdayEn: "saturday", label: "Sábado", weekday: "Sábado" },
  { weekdayEn: "sunday", label: "Domingo", weekday: "Domingo" },
];

const linhaVazia = (d) => ({
  weekdayEn: d.weekdayEn,
  weekday: d.weekday,
  startTimeA: "",
  endTimeA: "",
  startTimeB: "",
  endTimeB: "",
});

/**
 * Modal de horarios do profissional: grade semanal (dois turnos/dia) e folgas.
 * Usa "api" (admin, tem sessao).
 */
const HorariosModal = ({ open, onClose, staff, onSaved }) => {
  const [grade, setGrade] = useState([]);
  const [offs, setOffs] = useState([]);
  const [novaFolga, setNovaFolga] = useState({
    startDate: "",
    endDate: "",
    reason: "",
  });
  const [savingGrade, setSavingGrade] = useState(false);

  // Monta os 7 dias a partir do que veio salvo, preenchendo os faltantes.
  useEffect(() => {
    if (!open || !staff) return;
    const salvos = Array.isArray(staff.schedules) ? staff.schedules : [];
    setGrade(
      DIAS.map((d) => {
        const s = salvos.find((x) => x && x.weekdayEn === d.weekdayEn);
        return s
          ? {
              weekdayEn: d.weekdayEn,
              weekday: d.weekday,
              startTimeA: s.startTimeA || "",
              endTimeA: s.endTimeA || "",
              startTimeB: s.startTimeB || "",
              endTimeB: s.endTimeB || "",
            }
          : linhaVazia(d);
      })
    );
  }, [open, staff]);

  const carregarFolgas = useCallback(() => {
    if (!staff?.id) return;
    api
      .get(`/staff-members/${staff.id}/time-off`)
      .then(({ data }) => setOffs(data || []))
      .catch(() => setOffs([]));
  }, [staff]);

  useEffect(() => {
    if (open) carregarFolgas();
  }, [open, carregarFolgas]);

  const setCampo = (weekdayEn, campo, valor) => {
    setGrade((prev) =>
      prev.map((d) => (d.weekdayEn === weekdayEn ? { ...d, [campo]: valor } : d))
    );
  };

  const copiarParaOutrosDias = () => {
    const seg = grade.find((d) => d.weekdayEn === "monday");
    if (!seg) return;
    setGrade((prev) =>
      prev.map((d) =>
        ["tuesday", "wednesday", "thursday", "friday"].includes(d.weekdayEn)
          ? {
              ...d,
              startTimeA: seg.startTimeA,
              endTimeA: seg.endTimeA,
              startTimeB: seg.startTimeB,
              endTimeB: seg.endTimeB,
            }
          : d
      )
    );
    toast.info("Segunda copiada para terça a sexta.");
  };

  const salvarGrade = () => {
    setSavingGrade(true);
    api
      .put(`/staff-members/${staff.id}/schedules`, { schedules: grade })
      .then(() => {
        toast.success("Horários salvos.");
        if (onSaved) onSaved();
      })
      .catch(toastError)
      .finally(() => setSavingGrade(false));
  };

  const adicionarFolga = () => {
    if (!novaFolga.startDate || !novaFolga.endDate) {
      toast.error("Informe a data inicial e a final.");
      return;
    }
    api
      .post(`/staff-members/${staff.id}/time-off`, novaFolga)
      .then(() => {
        toast.success("Folga adicionada.");
        setNovaFolga({ startDate: "", endDate: "", reason: "" });
        carregarFolgas();
        if (onSaved) onSaved();
      })
      .catch((err) => {
        if (err?.response?.data?.error === "ERR_INVALID_DATE_RANGE") {
          toast.error("A data final não pode ser antes da inicial.");
        } else {
          toastError(err);
        }
      });
  };

  const removerFolga = (id) => {
    api
      .delete(`/staff-members/${staff.id}/time-off/${id}`)
      .then(() => {
        carregarFolgas();
        if (onSaved) onSaved();
      })
      .catch(toastError);
  };

  if (!staff) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>
        Horários — {staff.name}
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>Grade semanal</Typography>
          <Button size="small" onClick={copiarParaOutrosDias} sx={{ textTransform: "none" }}>
            Copiar para os outros dias
          </Button>
        </Box>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
          Deixe em branco os dias/turnos em que não atende. O profissional só é
          oferecido dentro do expediente da empresa.
        </Typography>

        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Dia</TableCell>
                <TableCell align="center">Manhã</TableCell>
                <TableCell align="center">Tarde</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grade.map((d) => (
                <TableRow key={d.weekdayEn}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {DIAS.find((x) => x.weekdayEn === d.weekdayEn)?.label}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                      <TextField
                        type="time"
                        size="small"
                        value={d.startTimeA}
                        onChange={(e) => setCampo(d.weekdayEn, "startTimeA", e.target.value)}
                        sx={{ width: 110 }}
                      />
                      <span>–</span>
                      <TextField
                        type="time"
                        size="small"
                        value={d.endTimeA}
                        onChange={(e) => setCampo(d.weekdayEn, "endTimeA", e.target.value)}
                        sx={{ width: 110 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                      <TextField
                        type="time"
                        size="small"
                        value={d.startTimeB}
                        onChange={(e) => setCampo(d.weekdayEn, "startTimeB", e.target.value)}
                        sx={{ width: 110 }}
                      />
                      <span>–</span>
                      <TextField
                        type="time"
                        size="small"
                        value={d.endTimeB}
                        onChange={(e) => setCampo(d.weekdayEn, "endTimeB", e.target.value)}
                        sx={{ width: 110 }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ textAlign: "right", mt: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            onClick={salvarGrade}
            disabled={savingGrade}
          >
            {savingGrade ? "Salvando..." : "Salvar grade"}
          </Button>
        </Box>

        <Typography sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
          Folgas e férias
        </Typography>

        {offs.length ? (
          <Table size="small" sx={{ mb: 1.5 }}>
            <TableBody>
              {offs.map((o) => (
                <TableRow key={o.id}>
                  <TableCell>
                    {moment(o.startDate).format("DD/MM/YYYY")} a{" "}
                    {moment(o.endDate).format("DD/MM/YYYY")}
                    {o.reason ? ` — ${o.reason}` : ""}
                  </TableCell>
                  <TableCell align="right" sx={{ width: 48 }}>
                    <IconButton size="small" onClick={() => removerFolga(o.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.5 }}>
            Nenhuma folga cadastrada.
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            type="date"
            size="small"
            label="De"
            InputLabelProps={{ shrink: true }}
            value={novaFolga.startDate}
            onChange={(e) => setNovaFolga((p) => ({ ...p, startDate: e.target.value }))}
          />
          <TextField
            type="date"
            size="small"
            label="Até"
            InputLabelProps={{ shrink: true }}
            value={novaFolga.endDate}
            onChange={(e) => setNovaFolga((p) => ({ ...p, endDate: e.target.value }))}
          />
          <TextField
            size="small"
            label="Motivo (opcional)"
            value={novaFolga.reason}
            onChange={(e) => setNovaFolga((p) => ({ ...p, reason: e.target.value }))}
          />
          <Button variant="outlined" size="small" onClick={adicionarFolga}>
            Adicionar folga
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HorariosModal;
